// The opened card's "In their words" and Value (pipeline P9, REQ-2026-10-05-14; CLIENT-WORDS-AND-VALUE-BRIEF-2026-10-05.md
// section 4, drawn in screens/2026-10-05-add-deal-concepts/c-card.html; module 10 U294 to U297). The words come from
// client-words.ts, the data from the heat read's live part (GET /api/deals/heat), so Deals and the company record draw
// the same card.
//
// U152 is the rule: a control is drawn only where its route exists. Value's Change opens How it pays as the brief lists
// its fields (section 4, "How it pays (the form and the card's Value)"), each written through POST /api/deals/:id/pay and,
// for "What we expect to earn this year", POST /api/deals/:id/figure; Undo takes back both.
import { ActionSelect, ActionInput, ActionButton } from '../parts/index.js';
import { useState } from 'react';
import { dealsApi, DealsError, type HeatRow, type PayInput, type PayTerms, type SaidLine } from './api.js';
import { baseLabel, baseWords, channelChip, channelOf, conversationWords, earlierWords, entryWho, figureByWords, howItPays, minimumWords, moveNote, nothingYetWords, openWords, questionWords, recordDay, statementWords, theirWords } from './client-words.js';
import { moneyFull } from './heat-words.js';
import { opensPath, senderWords, subjectWords } from './live-words.js';
import type { LiveContext, Saved } from './LiveCard.js';

/** The first entries "In their words" shows (c-card.html: "The first four"). */
export const ENTRIES_SHOWN = 4;

interface Entry { key: string; at: string; side: 'them' | 'us' | 'unread'; chip: string; who: string; when: string; words: string; path: string | null; open: string }

/**
 * "In their words", "and what we said back, newest first" (U297): each message filed under the deal, newest first, as what
 * was said in it once Cobrain has read it; theirs in ink and in quotes with "Open the call", ours under "We said" in ink-2.
 * A message not read yet is listed as itself, by who sent it and its subject: the same messages Latest listed (the brief).
 * Their newest words take their place by date when no listed message holds them, so the card says what its line says.
 * The notes' words close the list. The first four, then the whole conversation on the history page.
 */
export function InTheirWords({ row, ctx, accountName, showName }: { row: HeatRow; ctx: LiveContext; accountName: string | null; showName: string | null }) {
  const live = row.live;
  const heard = live?.heard ?? (live?.latest ?? []).map((m) => ({ message: m, said: [] as SaidLine[] }));
  const entries: Entry[] = [];
  for (const h of heard) {
    const m = h.message;
    const c = channelOf(m.kind);
    const path = opensPath(m.signalId, m.opens, ctx.store);
    if (h.said.length === 0) {
      entries.push({ key: m.signalId, at: m.at, side: 'unread', chip: channelChip(c), who: senderWords(m), when: recordDay(m.at, ctx.dayStart), words: subjectWords(m) ?? '', path, open: openWords(c) });
      continue;
    }
    for (const l of h.said) {
      entries.push({
        key: `${m.signalId}-${l.side}`, at: l.at, side: l.side, chip: channelChip(l.channel), who: entryWho(l), when: recordDay(l.at, ctx.dayStart),
        words: l.side === 'them' ? theirWords(l.words, 400) : l.words, path: opensPath(m.signalId, l.opens ?? m.opens, ctx.store), open: openWords(l.channel),
      });
    }
  }
  // Their newest words, which the line quotes, in their place when the messages listed do not hold them: a person's own
  // record of what they said, or a message older than the newest listed. The card is never emptier than its line.
  const last = live?.theirLast ?? null;
  if (last && !entries.some((e) => e.key === `${last.signalId}-them`)) {
    const e: Entry = { key: 'last', at: last.at, side: 'them', chip: channelChip(last.channel), who: entryWho(last), when: recordDay(last.at, ctx.dayStart),
      words: theirWords(last.words, 400), path: last.signalId ? opensPath(last.signalId, last.opens, ctx.store) : null, open: openWords(last.channel) };
    const at = entries.findIndex((x) => x.at < last.at);
    entries.splice(at === -1 ? entries.length : at, 0, e);
  }
  const notes = row.clientWords?.from === 'sheet' ? row.clientWords : null;
  if (notes) entries.push({ key: 'notes', at: '', side: 'them', chip: 'Notes', who: showName ? `${showName} notes` : 'The notes', when: '', words: theirWords(notes.text, 400), path: null, open: '' });
  const total = live?.messages ?? 0;
  return (
    <div className="lv-words">
      <h4>In their words<small>and what we said back, newest first</small></h4>
      {entries.length === 0
        ? <p className="lv-wnone">{nothingYetWords(accountName)}</p>
        : (
          <ul>
            {entries.slice(0, ENTRIES_SHOWN).map((e) => (
              // w-: a bare "unread" is the messenger's red badge, page-wide (messenger.css), and drew every unread entry as one.
              <li key={e.key} className={`w-${e.side}`}>
                <span className="ch">{e.chip}</span>
                <div>
                  <div className="m">
                    {e.side === 'us' ? <b>We said</b> : <b>{e.who}</b>}
                    <span>{e.side === 'us' ? `${e.who}${e.when ? `, ${e.when}` : ''}` : e.when}</span>
                    {e.path ? <ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(e.path!)}>{e.open}</ActionButton> : null}
                  </div>
                  {e.words ? <p>{e.words}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      {total > 0
        ? <ActionButton reading type="button" className="hx-link x" onClick={() => ctx.open(`/deals/${encodeURIComponent(row.id)}`)}>{conversationWords(total)}</ActionButton>
        : null}
    </div>
  );
}

const OF_CHOICES: Array<[NonNullable<PayTerms['shareOf']>, string]> = [['ggr', 'GGR'], ['ngr', 'NGR'], ['turnover', 'turnover'], ['net_revenue', 'net revenue']];
/** "Potential NGR share", as Add deal names the box (B2282). */
const OF_SHARE: Record<NonNullable<PayTerms['shareOf']>, string> = { ggr: 'GGR', ngr: 'NGR', turnover: 'turnover', net_revenue: 'net revenue' };
const BASES: Array<[PayTerms['basis'], string]> = [['fixed', 'Fixed fee'], ['share', 'Revenue share'], ['per_match', 'Per match'], ['unknown', 'Not known yet']];
const num = (s: string): number | null => {
  const raw = s.replace(/[,\s£€$%]/g, '');
  return raw !== '' && /^\d+(\.\d{1,3})?$/.test(raw) ? Number(raw) : null;
};
const yearOf = (amount: number | null, period: string): number | null => (amount === null ? null : period === 'month' ? amount * 12 : amount);
const r2 = (x: number) => Math.round(x * 100) / 100;

/**
 * What the terms make the year, as the brief's form works it out: a fixed fee a year; per match, the rate times the matches;
 * a revenue share, the minimum plus our potential share above it (Mike, 7 Oct 2026: "The MG is the MG, when we quote an
 * estimate of the rev share, this means the amount over and above the mg"; module 04 B2282), or either alone. Null when
 * the terms do not give one.
 */
export function workedYear(f: { basis: PayTerms['basis']; fee: string; feePeriod: string; above: string; abovePeriod: string; min: string; minPeriod: string; rate: string; count: string }): { amount: number; held: boolean; worked: number | null } | null {
  const min = yearOf(num(f.min), f.minPeriod);
  if (f.basis === 'fixed') { const y = yearOf(num(f.fee), f.feePeriod); return y === null ? null : { amount: r2(y), held: false, worked: null }; }
  if (f.basis === 'per_match') {
    const rate = num(f.rate); const count = num(f.count);
    const y = rate !== null && count !== null ? rate * count : null;
    if (y === null) return min !== null ? { amount: r2(min), held: true, worked: null } : null;
    return min !== null && y < min ? { amount: r2(min), held: true, worked: r2(y) } : { amount: r2(y), held: false, worked: null };
  }
  if (f.basis === 'share') {
    const above = yearOf(num(f.above), f.abovePeriod);
    if (above === null && min === null) return null;
    return { amount: r2((min ?? 0) + (above ?? 0)), held: false, worked: null };
  }
  return null;
}

/**
 * Expected revenues, as the brief lists the form's fields ("How it pays" until Mike, 7 Oct 2026: "how it pays is naff,
 * change this for expected revenues", and by pop-up the same day, "Rename it on the card too"; B2251), with "What we
 * expect to earn this year" marked Estimate.
 */
function PayForm({ row, ctx, onDone, onCancel }: { row: HeatRow; ctx: LiveContext; onDone: (s: Saved) => void; onCancel: () => void }) {
  const p = row.live?.value?.pay ?? null;
  // A deal whose terms nobody has given opens on "Not known yet": a sheet's figure says nothing of how it pays.
  const [basis, setBasis] = useState<PayTerms['basis']>(p?.basis ?? 'unknown');
  const [currency, setCurrency] = useState(p?.currency ?? row.figure?.currency ?? ctx.homeCurrency ?? 'GBP');
  const [fee, setFee] = useState(p?.feeAmount ? String(Number(p.feeAmount)) : '');
  const [feePeriod, setFeePeriod] = useState<string>(p?.feePeriod ?? 'year');
  const [pct, setPct] = useState(p?.sharePct ? String(Number(p.sharePct)) : '');
  const [of, setOf] = useState<NonNullable<PayTerms['shareOf']>>(p?.shareOf ?? 'ngr');
  const [min, setMin] = useState(p?.minimumAmount ? String(Number(p.minimumAmount)) : '');
  const [minPeriod, setMinPeriod] = useState<string>(p?.minimumPeriod ?? 'year');
  // Our potential share above the minimum: what the year's figure holds over the minimum, on a share with both (B2282).
  const minYear = p?.minimumAmount ? yearOf(Number(p.minimumAmount), p.minimumPeriod ?? 'year') : null;
  const aboveWas = p?.basis === 'share' && row.figure && minYear !== null && Number(row.figure.amount) > minYear ? String(r2(Number(row.figure.amount) - minYear)) : '';
  const [above, setAbove] = useState(aboveWas);
  const [abovePeriod, setAbovePeriod] = useState<string>('year');
  const [rate, setRate] = useState(p?.matchRate ? String(Number(p.matchRate)) : '');
  const [count, setCount] = useState(p?.matchCount ? String(p.matchCount) : '');
  const worked = workedYear({ basis, fee, feePeriod, above, abovePeriod, min, minPeriod, rate, count });
  const was = row.figure ? String(Number(row.figure.amount)) : '';
  const [expect, setExpect] = useState<string | null>(null);
  const expectShown = expect ?? (worked ? String(worked.amount) : was);
  // A cleared estimate falls back to Cobrain's working, as on Add deal (B2282): emptied, the box shows it as its placeholder.
  const expectSaved = expectShown.trim() !== '' ? expectShown : worked ? String(worked.amount) : '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choices = [...new Set([currency, ...ctx.currencies])];

  const save = () => {
    const money = (s: string, what: string): number | null | 'bad' => { if (!s.trim()) return null; const n = num(s); return n === null ? (setError(`${what} is not an amount.`), 'bad') : n; };
    const f = money(fee, 'The fee'); const m = money(min, 'The minimum'); const ab = money(above, 'The potential share'); const rt = money(rate, 'The rate'); const ex = money(expectSaved, 'What we expect');
    if ([f, m, ab, rt, ex].includes('bad')) return;
    const share = num(pct);
    const pay: PayInput = {
      basis, currency,
      fee: basis === 'fixed' && f !== null ? { amount: f as number, period: feePeriod as 'year' | 'month' } : null,
      share: basis === 'share' && share !== null ? { pct: share, of } : null,
      minimum: (basis === 'share' || basis === 'per_match') && m !== null ? { amount: m as number, period: minPeriod as 'year' | 'month' } : null,
      match: basis === 'per_match' && rt !== null ? { rate: rt as number, count: num(count) } : null,
      // Their base is no longer asked for (B2282): the potential share is ours, and the year's figure carries it.
      base: null,
    };
    if (basis === 'unknown') pay.currency = null;
    // Terms nobody gave, left at "Not known yet", are not written: only what we expect is.
    const payToo = !(p === null && basis === 'unknown');
    setBusy(true); setError(null);
    (payToo ? dealsApi.setPay(ctx.store, row.id, pay) : Promise.resolve(null))
      .then(async (r) => {
        const figure = ex as number | null;
        const changed = figure !== null && (row.figure === null || Number(row.figure.amount) !== figure || row.figure.currency !== currency);
        const fw = changed ? await dealsApi.setFigure(ctx.store, row.id, figure, currency) : null;
        setBusy(false);
        const ids = [fw?.changeId, r?.changeId].filter((x): x is string => !!x);
        const words = changed && r ? 'You set the expected revenues and what we expect just now.' : changed ? 'You set what we expect just now.' : 'You set the expected revenues just now.';
        if (ids.length > 0) onDone({ field: 'pay', changeId: ids[ids.length - 1]!, also: ids.slice(0, -1), words });
        else onCancel();
        ctx.refresh({ accountId: row.accountId, dealId: row.id });
      })
      .catch((e: unknown) => { setBusy(false); setError(`It did not save${e instanceof DealsError ? `: ${e.message}` : ''}. What you typed is still here.`); });
  };

  const period = (v: string, set: (x: string) => void, label: string) => (
    <ActionSelect aria-label={label} value={v} onChange={(e) => set(e.target.value)}><option value="year">a year</option><option value="month">a month</option></ActionSelect>
  );
  const amount = (v: string, set: (x: string) => void, label: string, ph = 'amount') => (
    <ActionInput type="text" inputMode="decimal" aria-label={label} value={v} placeholder={ph} onChange={(e) => set(e.target.value)} />
  );
  return (
    <div className="lv-payform" role="group" aria-label="Expected revenues">
      <div className="lv-bases" role="radiogroup" aria-label="Expected revenues">
        {BASES.map(([k, l]) => (
          <ActionButton type="button" key={k} role="radio" aria-checked={basis === k} className={basis === k ? 'opt on' : 'opt'} onClick={() => setBasis(k)}>{l}</ActionButton>
        ))}
      </div>
      {basis !== 'unknown' || expectShown.trim() !== ''
        ? (
          <label className="lv-pf"><span className="l">Currency</span>
            {choices.length > 1 ? <ActionSelect aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>{choices.map((c) => <option key={c} value={c}>{c}</option>)}</ActionSelect> : <span className="cur">{currency}</span>}
          </label>
        )
        : null}
      {basis === 'fixed' ? <label className="lv-pf"><span className="l">Fixed fee</span>{amount(fee, setFee, 'Fixed fee')}{period(feePeriod, setFeePeriod, 'Fee period')}</label> : null}
      {basis === 'share'
        ? (
          <>
            <label className="lv-pf"><span className="l">Share</span><ActionInput type="text" inputMode="decimal" aria-label="Share, per cent" value={pct} placeholder="%" onChange={(e) => setPct(e.target.value)} /><span className="u">%</span></label>
            <label className="lv-pf"><span className="l">Of</span><ActionSelect aria-label="Of" value={of} onChange={(e) => setOf(e.target.value as typeof of)}>{OF_CHOICES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</ActionSelect></label>
          </>
        )
        : null}
      {basis === 'per_match'
        ? (
          <>
            <label className="lv-pf"><span className="l">Per match</span>{amount(rate, setRate, 'Rate a match')}<span className="u">a match</span></label>
            <label className="lv-pf"><span className="l">About</span><ActionInput type="text" inputMode="numeric" aria-label="Matches a year" value={count} placeholder="n" onChange={(e) => setCount(e.target.value)} /><span className="u">matches a year</span></label>
          </>
        )
        : null}
      {basis === 'share' || basis === 'per_match'
        ? <label className="lv-pf"><span className="l">Minimum guarantee</span>{amount(min, setMin, 'Minimum guarantee')}{period(minPeriod, setMinPeriod, 'Minimum period')}<span className="u">as proposed</span></label>
        : null}
      {basis === 'share'
        ? <label className="lv-pf"><span className="l">Potential {OF_SHARE[of]} share</span>{amount(above, setAbove, 'Potential share')}{period(abovePeriod, setAbovePeriod, 'Potential share period')}<span className="u">on top of the minimum</span></label>
        : null}
      <label className="lv-pf"><span className="l">What we expect to earn this year</span>{amount(expectShown, (x) => setExpect(x), 'What we expect to earn this year', worked ? worked.amount.toLocaleString('en-GB') : 'amount')}<span className="chip">Estimate</span></label>
      <div className="acts">
        <ActionButton type="button" className="opt lead" disabled={busy} onClick={save}>Save</ActionButton>
        <ActionButton type="button" className="hx-link m" onClick={onCancel}>Cancel</ActionButton>
      </div>
      {error ? <p className="lv-err" role="alert">{error} <ActionButton type="button" className="hx-link" onClick={save}>Try again</ActionButton></p> : null}
    </div>
  );
}

/**
 * Value (U294, U295; c-card.html): how it pays, the minimum "as proposed", their base with who gave it, the statements read,
 * and "We expect [£] a year" with the Estimate chip; under it Cobrain's last move with its sum, "Open the call" and Undo,
 * or after Undo "Back to [£] a year"; the owner's question when a view had no figure; and earlier values on request.
 */
export function ValueBlock({ row, ctx, onSaved, onNote, showName }: { row: HeatRow; ctx: LiveContext; onSaved: (s: Saved) => void; onNote: (w: string | null) => void; showName: string | null }) {
  const [changing, setChanging] = useState(false);
  const [earlierOpen, setEarlierOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = row.live?.value ?? { pay: null, base: null, move: null, earlier: [], statements: [], question: null };
  const stage = row.live?.stageCode ?? null;
  const minimum = minimumWords(v.pay, stage);
  const base = v.pay?.basis === 'share' ? baseWords(v, ctx.dayStart) : null;
  const statements = statementWords(v, ctx.dayStart);
  const note = moveNote(v, row, ctx.now, ctx.dayStart);
  const earlier = earlierWords(v.earlier, showName, ctx.dayStart);
  const figureBy = figureByWords(row.figure, note !== null && !v.move?.undone, showName, ctx.dayStart);
  const movePath = v.move?.message ? opensPath(v.move.message.signalId, v.move.message.opens, ctx.store) : null;
  const q = v.question && v.question.mine ? v.question : null;
  const qPath = q?.message ? opensPath(q.message.signalId, q.message.opens, ctx.store) : null;
  const undo = () => {
    if (!v.move) return;
    setBusy(true); setError(null);
    dealsApi.undoChange(ctx.store, v.move.changeId)
      .then(() => { setBusy(false); onNote(null); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); setError(e instanceof DealsError ? `Nothing changed: ${e.message}.` : 'Nothing changed. Try again.'); });
  };
  return (
    <div className="lv-value">
      <h4>Value{ctx.mayEdit && !changing ? <ActionButton type="button" className="hx-link" onClick={() => setChanging(true)}>Change</ActionButton> : null}</h4>
      {changing
        ? <PayForm row={row} ctx={ctx} onCancel={() => setChanging(false)} onDone={(s) => { setChanging(false); onSaved(s); }} />
        : (
          <ul className="will">
            <li><span className="k">Expected revenues</span><span className="v">{howItPays(v.pay)}</span></li>
            {minimum ? <li><span className="k">Minimum</span><span className="v">{minimum}</span></li> : null}
            {base ? <li><span className="k">{baseLabel(v.pay)}</span><span className="v">{base}</span></li> : null}
            {statements ? <li><span className="k">Statements</span><span className="v">{statements}</span></li> : null}
            {/* BUGS.md D151: who set the figure, as Earlier values and the base name theirs; Cobrain's own move says it below. */}
            <li><span className="k">We expect</span><span className="v">{row.figure ? <><b>{moneyFull(row.figure.amount, row.figure.currency)} a year</b> <span className="chip">Estimate</span>{figureBy ? <> {figureBy}</> : null}</> : <b>No figure yet</b>}</span></li>
          </ul>
        )}
      {note
        ? (
          <div className="note">
            {note.text}
            {movePath && note.open ? <> <ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(movePath)}>{note.open}</ActionButton></> : null}
            {note.undo && ctx.mayEdit ? <> <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>Undo</ActionButton></> : null}
          </div>
        )
        : null}
      {q
        ? (
          <div className="note q">
            {questionWords(q)} {ctx.mayEdit ? <ActionButton type="button" className="hx-link" onClick={() => setChanging(true)}>New figure?</ActionButton> : 'New figure?'}
            {qPath && q.message ? <> <ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(qPath)}>{openWords(channelOf(q.message.kind))}</ActionButton></> : null}
          </div>
        )
        : null}
      {earlier
        ? (earlierOpen
          ? <p className="lv-hint">{earlier}</p>
          : <ActionButton reading type="button" className="hx-link m" onClick={() => setEarlierOpen(true)}>Earlier values</ActionButton>)
        : null}
      {error ? <p className="lv-err" role="alert">{error}</p> : null}
    </div>
  );
}
